import { Address } from './Address';
import { CompanyReference } from './contacts/CompanyStore';
import {
  types,
  Instance,
  SnapshotIn,
  SnapshotOut,
  getRoot,
} from 'mobx-state-tree';
import { Intents } from '../npaper';
import { modelWithCRUD } from './utils/modelWithCRUD';
import { ContactReference } from './contacts/ContactStore';
import { SaleItemReference } from './SaleItemStore';
import { RootModelType } from './Root';
import { DateTime } from './DateTime';
import { CommomMixin, ResponsibleMixin } from './Mixins';
import { CostPrice } from './products/ProductStore';

export enum AgreementStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

export enum IntervalUnit {
  DAYS = 'days',
  WEEKS = 'weeks',
  MONTHS = 'months',
  YEARS = 'years',
}

export const Interval = types.model('Interval', {
  every: types.number,
  unit: types.enumeration<IntervalUnit>(Object.values(IntervalUnit)),
});

export const Agreement = types
  .compose(
    CommomMixin,
    ResponsibleMixin,
    types.model({
      id: types.union(types.identifier, types.identifierNumber),
      title: types.string,
      status: types.enumeration<AgreementStatus>(
        Object.values(AgreementStatus)
      ),

      customer: types.late(() => CompanyReference),
      contacts: types.array(types.late(() => ContactReference)),
      invoice_address: types.maybe(Address),
      delivery_address: types.maybe(Address),
      directDelivery: types.optional(types.boolean, false),

      currency: types.optional(types.string, 'DKK'),
      price: types.number, // Computed on API
      cost_price: CostPrice,
      contribution_margin: CostPrice,
      contribution_margin_ratio: CostPrice,

      lines: types.array(types.late(() => SaleItemReference)),

      interval: Interval,
      nextCycleDate: types.optional(DateTime, () => new Date()),
      startDate: types.optional(DateTime, () => new Date()),
      endDate: types.maybeNull(DateTime),
    })
  )
  .named('Agreement')
  .views((self) => ({
    get statusIntent(): Intents {
      return self.status === AgreementStatus.DRAFT
        ? 'none'
        : self.status === AgreementStatus.ACTIVE
        ? 'success'
        : self.status === AgreementStatus.CANCELLED
        ? 'danger'
        : self.status === AgreementStatus.EXPIRED
        ? 'info'
        : 'none';
    },
    get intervalTitle(): string {
      const every = self.interval.every === 1 ? '' : self.interval.every + '. ';
      return self.interval.unit === IntervalUnit.DAYS
        ? `Hver ${every}dag`
        : self.interval.unit === IntervalUnit.WEEKS
        ? `Hver ${every}uge`
        : self.interval.unit === IntervalUnit.MONTHS
        ? `Hver ${every}måned`
        : self.interval.unit === IntervalUnit.YEARS
        ? `Hver ${every}år`
        : `Hver ${every}UKENDT`;
    },
  }));

export const AgreementStore = modelWithCRUD('agreements', Agreement).named(
  'AgreementStore'
);

export interface IAgreement extends Instance<typeof Agreement> {}
export interface IAgreementSnapshotIn extends SnapshotIn<typeof Agreement> {}
export interface IAgreementSnapshotOut extends SnapshotOut<typeof Agreement> {}

export const AgreementReference = types.safeReference(Agreement, {
  get(identifier, parent): any {
    return (getRoot(parent!) as RootModelType).agreementStore.getItemNaive(
      identifier
    );
  },
  set(value) {
    return value.id;
  },
});
